import { MouseEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Button from '@material-ui/core/Button';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { t } from 'src/lib/language';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import ProductValidator from 'src/validations/ProductValidator';
import { INVOICE_FIELDS } from 'src/constants/Invoices';
import { ProductModel, ProductType } from 'src/models/ProductModel';
import MetadataSelector, { MetadataType } from 'src/components/Control/MetadataSelector';
import { putProductAction } from 'src/redux/actions/productAction';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import { Tooltip } from '@material-ui/core';
import TaxSelector from 'src/components/Control/TaxSelector';
import OrganisationBankAccountSelector
  from 'src/components/Control/OrganisationBankAccountSelector';
import { setFormStatus } from 'src/redux/actions/formActions';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import SaveIcon from '@material-ui/icons/Save';
import BackspaceIcon from '@material-ui/icons/Backspace';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/UI/ActionButton';
import ProductTypeSelector from 'src/components/Control/ProductTypeSelector';
import TextLink from 'src/components/UI/TextLink';
import { openModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { ProcessingType } from 'src/models/GatewayModel';
import Form from 'src/components/UI/Form';
import Header from 'src/components/UI/Header';
import Title from 'src/components/UI/Title';
import Row from 'src/components/UI/Row';
import Subtitle from 'src/components/UI/Subtitle';
import BottomRow from 'src/components/UI/BottomRow';
import Scroller from 'src/components/UI/Scroller';
import SubmenuLink from 'src/components/UI/SubmenuLink';

interface Props {
  onClose?: () => void;
  product: ProductModel;
}

const EditProductForm = (props: Props) => {
  const dispatch = useAppDispatch();
  const formStatus = useSelector((state: Store) => state.formStatus[FORM.put_product] ?? null);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const { onClose, product } = props;

  const [state, setState] = useState({
    ...product,
    deductible: (!!product.deductible) ? `${Number(product.deductible) * 100}` : '0',
  });
  const form = FORM.manage_product;

  useEffect(() => {
    dispatch(setError({ [form]: {} }));
  }, []);

  useEffect(() => {
    if (formStatus === FORM_STATUS.success) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onClose && onClose();
      dispatch(setFormStatus({ [FORM.put_product]: null }));
    }
  }, [formStatus]);

  const onChange = (value: any, field: string) => {
    setState({
      ...state,
      [field]: value,
    });
  };

  const getMimeType = (base64Data: string) => {
    // Check JPEG magic bytes (starts with /9j/)
    if (base64Data.startsWith('/9j/')) {
      return 'image/jpeg';
    }
    // Check PNG magic bytes (starts with iVBORw0KGgo)
    if (base64Data.startsWith('iVBORw0KGgo')) {
      return 'image/png';
    }
    return 'image/png'; // default fallback
  };

  const onPictureChange = (value: string) => {
    const mimeType = getMimeType(value);
    const file = `data:${mimeType};base64,${value}`;
    onChange(file, 'picture');
  };

  const onUploadImage = () => {
    dispatch(openModal({
      modal: MODALS.uploadImage,
      payload: {
        path: '',
        size: {
          width: 250,
          height: 250,
          ratio: 1,
        },
        name: '',
        onSave: onPictureChange,
      },
    }));
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [form]: {} }));
    try {
      ProductValidator(lang).validateSync(state, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'forms.group.validation_errors',
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [form]: errorBag }));
      return false;
    }
    dispatch(putProductAction({
      ...state,
      allow_installment: state.allow_installment ? 1 : 0,
      deductible: Number(state.deductible ?? 0) * 0.01,
    } as ProductModel));
    return true;
  };

  const onIdentifiersChange = (value: string) => {
    setState({
      ...state,
      identifiers: value,
    });
  };

  const manageIdentifiers = () => {
    dispatch(
      openModal({
        modal: MODALS.ticketIdentifiers,
        payload: [onIdentifiersChange, state.identifiers],
      }),
    );
  };

  const CloseButton = () => (
    <Button
      style={{ marginLeft: 'auto' }}
      variant="contained"
      color="primary"
      size="small"
      onClick={handleClose}>
      Close
    </Button>
  );

  const SaveButton = () => (
    <Tooltip
      title={t(lang, 'menus.save')}
      placement="top-start">
      <span>
        <ActionButton
          loading={formStatus === FORM_STATUS.processing}
          category={MENU_BUTTON_CATEGORY.action}
          onClick={handleSubmit}>
          <SaveIcon />
        </ActionButton>
      </span>
    </Tooltip>
  );

  const BackButton = () => (
    <Tooltip
      title={t(lang, 'misc.back_to_list')}
      placement="top-start">
      <span>
        <ActionButton
          category={MENU_BUTTON_CATEGORY.action}
          onClick={handleClose}>
          <BackspaceIcon />
        </ActionButton>
      </span>
    </Tooltip>
  );

  return (
    <Form>
      <Header height="82px">
        <Row>
          <Title><b>{ t(lang, 'menus.edit_product') }</b></Title>
          <CloseButton />
        </Row>
        <Row>
          <SaveButton />
          <BackButton />
        </Row>
      </Header>
      <Scroller height="633px">
        <FormCheckbox
          checked={state.is_managed}
          label={t(lang, 'forms.products.is_managed')}
          name="is_managed"
          onChange={onChange} />
        <FormCheckbox
          checked={state.is_modifiable}
          label={t(lang, 'forms.products.is_modifiable')}
          name="is_modifiable"
          onChange={onChange} />
        <FormCheckbox
          checked={state.is_public}
          label={t(lang, 'forms.products.is_public')}
          name="is_public"
          onChange={onChange} />
        <Subtitle><b>General Information</b></Subtitle>
        <FormTextField
          form={form}
          label={t(lang, 'forms.groups.name_en')}
          name="name_en"
          onChange={onChange}
          required
          value={state.name_en} />
        <FormTextField
          form={form}
          label={t(lang, 'forms.groups.name_fr')}
          name="name_fr"
          onChange={onChange}
          required
          value={state.name_fr} />
        <FormTextField
          form={form}
          label={t(lang, 'forms.groups.description_en')}
          name="description_en"
          onChange={onChange}
          required
          value={state.description_en} />
        <FormTextField
          form={form}
          label={t(lang, 'forms.groups.description_fr')}
          name="description_fr"
          onChange={onChange}
          required
          value={state.description_fr} />
        <ProductTypeSelector
          form={form}
          name="type"
          onChange={onChange}
          value={state.type} />
        { state.type === ProductType.ticket && (
        <>
          <FormCheckbox
            checked={state.last_name_required}
            label={t(lang, 'forms.products.last_name_required')}
            name="last_name_required"
            onChange={onChange} /><br />
          <FormCheckbox
            checked={state.first_name_required}
            label={t(lang, 'forms.products.first_name_required')}
            name="first_name_required"
            onChange={onChange} /><br />
          <FormCheckbox
            checked={state.email_required}
            label={t(lang, 'forms.products.email_required')}
            name="email_required"
            onChange={onChange} /><br />
          <FormCheckbox
            checked={state.phone_required}
            label={t(lang, 'forms.products.phone_required')}
            name="phone_required"
            onChange={onChange} /><br />
          <FormCheckbox
            checked={state.identifier_required}
            label={t(lang, 'forms.products.identifier_required')}
            name="identifier_required"
            onChange={onChange} />
          <TextLink onClick={manageIdentifiers}>Manage Identifiers</TextLink>
        </>
        )}
        <Subtitle><b>Pricing and Payments</b></Subtitle>
        <FormTextField
          form={form}
          label={t(lang, 'forms.products.price')}
          name="price"
          onChange={onChange}
          required
          value={state.price} />
        <FormTextField
          form={form}
          label={`${t(lang, 'forms.products.deductible')} (%)`}
          name="deductible"
          onChange={onChange}
          value={state.deductible}
          required />
        <FormTextField
          form={form}
          label={t(lang, 'forms.products.discount')}
          name="discount"
          onChange={onChange}
          value={state.discount} />
        <div style={{ marginTop: '8px' }}>
          <FormCheckbox
            checked={!!state.allow_installment}
            label={t(lang, 'forms.products.allow_installment')}
            name="allow_installment"
            onChange={onChange} />
        </div>
        { (!!state.allow_installment) && (
        <FormTextField
          form={form}
          label={t(lang, 'forms.groups.number_of_payments')}
          name="number_allowed_installments"
          onChange={onChange}
          value={state.number_allowed_installments}
          required />
        )}
        <TaxSelector
          form={form}
          name="taxes"
          onChange={(taxId: Number) => onChange(taxId, 'taxes')}
          size="medium"
          marginTop="8px"
          values={state.taxes} />
        <OrganisationBankAccountSelector
          value={state.bank_account_id || undefined}
          onChange={(id:number) => onChange(id, 'bank_account_id')}
          form={form}
          name="bank_account_id" />
        <GatewaySelector
          form={form}
          name="payment_gateway_eft_id"
          onChange={(value) => onChange(value, 'payment_gateway_eft_id')}
          value={state.payment_gateway_eft_id}
          type={ProcessingType.eft}
          noMarginTop />
        <GatewaySelector
          form={form}
          name="payment_gateway_cc_id"
          onChange={(value) => onChange(value, 'payment_gateway_cc_id')}
          value={state.payment_gateway_cc_id}
          type={ProcessingType.cc}
          noMarginTop />
        <Subtitle><b>Meta Information</b></Subtitle>
        <MetadataSelector
          type={MetadataType.program}
          onChange={(id) => onChange(id, 'program_id')}
          form={form}
          name="program_id"
          value={state.program_id || undefined}
          noMarginTop />
        <MetadataSelector
          type={MetadataType.department}
          onChange={(id) => onChange(id, 'department_id')}
          form={form}
          name="department_id"
          value={state.department_id || undefined}
          noMarginTop />
        <MetadataSelector
          type={MetadataType.account}
          onChange={(id) => onChange(id, 'account_id')}
          form={form}
          name="account_id"
          value={state.account_id || undefined}
          noMarginTop />
        <Subtitle><b>Store Information</b></Subtitle>
        <FormTextField
          form={form}
          label={t(lang, 'forms.products.quantity')}
          name="quantity"
          onChange={onChange}
          value={state.quantity} />
        <FormTextField
          form={form}
          label={t(lang, 'forms.products.cost')}
          name="cost"
          onChange={onChange}
          value={state.cost} />
        <FormTextField
          form={form}
          label="back_order"
          name="back_order"
          onChange={onChange}
          value={state.back_order} />
        <FormTextField
          form={form}
          label="SKU"
          name="sku"
          onChange={onChange}
          value={state.sku} />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '250px',
          height: '290px',
        }}>
          <div style={{
            height: '250px',
            width: '250px',
          }}>
            {state.picture && (
              <img
                src={`data:image/png;base64${state.picture?.replace(/[\r\n\s]/g, '')}`}
                alt="" />
            )}
          </div>
          <div>
            <SubmenuLink color="#0A4DF2" onClick={onUploadImage}>Upload Image</SubmenuLink>
          </div>
          <div style={{
            width: '100%',
            marginTop: '-10px',
          }}>
            <span style={{
              fontSize: '12px',
              fontStyle: 'italic',
            }}>
              250px X 250px | max 50Mb | (png or jpg)
            </span>
          </div>
        </div>
        <FormTextField
          form={form}
          label={t(lang, 'forms.invoices.note')}
          multiline
          minRows="4"
          name={INVOICE_FIELDS.note}
          onChange={onChange}
          value={state.note || ''} />
        <BottomRow>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '12px' }}
            onClick={handleSubmit}
            key="submit-button">
            {t(lang, 'misc.save')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '12px' }}
            onClick={handleClose}
            key={`submit-button-group-${state.id}`}>
            {t(lang, 'misc.back_to_list')}
          </Button>
        </BottomRow>
      </Scroller>
    </Form>
  );
};

export default EditProductForm;
